<script>
export default {
  name: "ExcursionsFulfillmentModal",
};
</script>

<script setup>
import { cloneDeep } from "lodash";
import { toRefs, ref, unref, computed, watch } from "vue";
import pLimit from "p-limit";
import orders from "@/services/orders";
import { useToast } from "vue-toast-notification";
import Dialog from "primevue/dialog";
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import BaseButton from "@/components/shared/Button/BaseButton.vue";
import CloseIcon from "@/components/svg-icons/CloseIcon.vue";
import PlusIcon from "@/components/shared/icons/PlusIcon";
import SimpleTextarea from "@/components/shared/SimpleTextarea.vue";
import { TrashIcon } from "@heroicons/vue/24/outline";
import { convertCost } from "@/components/program-manager/sessions/utils";
import { centsToDollars, dollarsToCents } from "@/mixins/helpers";
import { formatCalendarDate } from "@/util/calendarDate";
import { useQueryClient } from '@tanstack/vue-query'
import {
  EXCURSION_CANCELLATION_REASONS,
  CANCELLATION_REASONS_OTHER,
  ERROR_TIMEOUT,
  MAX_NOTE_TEXTAREA_LIMIT,
  SUCCESS_TIMEOUT,
  LIBRARY_IDS,
  OPTIONS_PRICE_OVERRIDE,
} from "@/constants";
import ExcursionsSelectionModal from "@/components/program-manager/sessions/components/ExcursionsAndEvents/ExcursionsSelectionModal.vue";
import Dropdown from "primevue/dropdown";
import InputNumber from 'primevue/inputnumber';
import { columnsConfig, ERROR_MESSAGE, EXCURSION_DATE_FORMAT, tableHeaders } from "./constants";
import { ORDER_PRODUCT_STATUS } from "@/constants";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { getOverrideReasons } from "@/composables/useOverrideReasons.js"
import { useCreatePriceOverride, getQueryKey as createKey } from "@/providers/priceOverrides/usePriceOverrideCreate";
import { usePatchPriceOverride, getQueryKey as patchKey } from "@/providers/priceOverrides/usePriceOverridePatch";
import { usePriceOverridesRevert } from "@/providers/priceOverrides/usePriceOverridesRevert";
import {
  lineItemPriceHasChanged,
  setPriceOverrideDropdownOption,
  lineItemHasPendingOverride,
  generatePriceOverrideOptions,
  generateDefaultPriceOverride,
  buildStatusRow,
  disableKeepCurrent,
  lineItemCurrentlyApproved,
  canCreatePriceOverride,
  canUpdatePriceOverride,
  findLatestPrice,
  showCurrentPrice
} from "@/components/StudentFulfillment/components/modals/utils.ts";
import { datadogLogs } from "@datadog/browser-logs";
import { OverrideReasons } from "@classes-service-types/override-reasons";

const props = defineProps({
  modalIsOpen: {
    type: Boolean,
    default: true,
  },
  orderId: {
    type: String,
    default: "",
  },
  profileName: {
    type: String,
    default: "",
  },
  excursionsData: {
    type: Array,
    default: () => [],
  },
  programSession: {
    type: Object,
    default: () => {},
  },
  loadingExcursions: {
    type: Boolean,
  },
  sessionPrices: {
    type: Object,
    default: () => {}
  }
});

const queryClient = useQueryClient();

const checkOtherCancellationReason = (cancellationReason) =>
  cancellationReason &&
  EXCURSION_CANCELLATION_REASONS.findIndex(({ id }) => id === cancellationReason) < 0;

const { orderId, modalIsOpen, loadingExcursions } = toRefs(props);
const updateExcursionsData = ref(cloneDeep(props.excursionsData));

const toast = useToast();
const plimit = pLimit(2);

const isSaving = ref(false);
const newExcursionsData = ref([]);

const excursionsSelectionModalIsOpen = ref(false);

const emit = defineEmits(["closeModal", "updateSavedExcursionData"]);

const statusOptions = computed(() => Object.values(ORDER_PRODUCT_STATUS) || []);

const exemptFromValidation = computed(() => {
  return new Set(
    updateExcursionsData.value.filter((excursion) => {
      // status hasn't changed and it was canceled by api
      return (
        excursion.cancellation_reason === OverrideReasons.ApiCanceledExcursion
        && excursion.status_before_changes === ORDER_PRODUCT_STATUS.canceled
        && excursion.status === ORDER_PRODUCT_STATUS.canceled
        && excursion.price_option === OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value
      )
    }).map((excursion => excursion.id))
  );
});

const buildDateRange = (excursion) => {
  const startDate = formatCalendarDate(excursion.start_date_time, EXCURSION_DATE_FORMAT);
  const endDate = formatCalendarDate(excursion.end_date_time, EXCURSION_DATE_FORMAT);
  return `${startDate} - ${endDate}`;
};

const rulesNewExcursions = {
  $each: helpers.forEach({
    excursion: {
      required: helpers.withMessage(
        "Please, provide a valid product to be added",
        required
      ),
    },
  }),
};

const rulesUpdateExcursions = {
  $each: helpers.forEach({
    cancellation_reason: {
      requiredIfCanceled: helpers.withMessage(
        "Cancellation reason is required when status is Canceled",
        (value, parentVm) => (parentVm.status === "Canceled" ? !!value : true)
      ),
    },
    otherCancellationReason: {
      requiredIfCanceledAndOther: (value, parentVm) =>
        parentVm.status === "Canceled" &&
        parentVm.cancellation_reason === "Other"
          ? !!value
          : true,
    },
  }),
};

const v$ = useVuelidate(rulesNewExcursions, newExcursionsData);
const updateV$ = useVuelidate(rulesUpdateExcursions, updateExcursionsData);

const savingErrorToast = (message) => {
  toast.open({
    message: `${message ?? ERROR_MESSAGE}`,
    type: "error",
    position: "bottom",
    duration: ERROR_TIMEOUT,
  });
};

const handleCloseModal = () => {
  clearNewExcursionsData();
  emit("closeModal");
};

const openExcursionsSelectionModal = () => {
  excursionsSelectionModalIsOpen.value = true;
};

const { mutateAsync: createPriceOverrideRequest } = useCreatePriceOverride({
  onSuccess: async (data, variables) => {
    await queryClient.invalidateQueries({ queryKey: createKey() });
    toast.open({
      message: `Created Price Override for ${variables.name}`,
      type: "success",
      position: "bottom",
      duration: SUCCESS_TIMEOUT,
    });
  },
  onError: (error, variables) => {
    datadogLogs.logger.warn("Failed to create price override.", {
      error: error.message,
    });
    toast.open({
      message: `Couldn't create price override for ${variables.name}`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  },
});
const { mutateAsync: patchPriceOverrideRequest } = usePatchPriceOverride({
  onSuccess: async ({ id }, variables) => {
    await queryClient.invalidateQueries({ queryKey: patchKey(id) });
    toast.open({
      message: `Updated Price Override for ${variables.name}`,
      type: "success",
      position: "bottom",
      duration: SUCCESS_TIMEOUT,
    });
  },
  onError: (error, variables) => {
    datadogLogs.logger.warn("Failed to update price override.", {
      error: error.message,
    });
    toast.open({
      message: `Couldn't update price override for ${variables.name}`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  },
});
const { mutateAsync: revertPriceOverrideRequest } = usePriceOverridesRevert({
    onSuccess: async (data, variables) => {
      if (Array.isArray(data?.errors) && data.errors.length > 0) {
        datadogLogs.logger.warn("Failed to revert price override for ids.", {
          errors: data?.errors,
        });
        toast.open({
          message: `Couldn't revert Price Override for ${variables?.name}`,
          type: "error",
          position: "bottom",
          duration: SUCCESS_TIMEOUT,
        });
      } else {
        toast.open({
          message: `Reverted Price Override for ${variables?.name}`,
          type: "success",
          position: "bottom",
          duration: SUCCESS_TIMEOUT,
        });
      }
    },
    onError: (error, variables) => {
      datadogLogs.logger.warn("Failed calling revert price override.", {
        error: error.message,
        priceOverrideId: variables?.payload ?? "unknown",
      });
      toast.open({
        message: `Couldn't revert Price Override for ${variables?.name}`,
        type: "error",
        position: "bottom",
        duration: SUCCESS_TIMEOUT,
      });
    },
});


const createPriceOverride = async (line_item) => {
  const createResponse = await createPriceOverrideRequest({
    payload: {
      request_notes: line_item.price_override.request_notes,
      override_amount_in_cents: dollarsToCents(line_item.price_override.override_amount_in_dollars),
      order_id: line_item.price_override.order_id,
      product_id: line_item.id,
      override_reason_id: line_item.price_override.override_reason_id,
      library_id: line_item.price_override.library_id,
    },
    name: line_item?.name,
  });
  return createResponse;
};

const patchPriceOverride = async (line_item) => {
  const updateResponse = await patchPriceOverrideRequest({
    id: line_item.price_override.id,
    payload: {
      override_amount_in_cents: dollarsToCents(line_item.price_override.override_amount_in_dollars),
      override_reason_id: line_item.price_override.override_reason_id,
      request_notes: line_item.price_override.request_notes,
    },
    name: line_item?.name,
  });
  return updateResponse;
};

const revertPriceOverride = async (line_item) => {
  const revertResponse = await revertPriceOverrideRequest({
    payload: {
      decision_notes: "User reverted to original price.",
      id: line_item?.latest_approved_price_override?.id
    },
    name: line_item?.name,
  });
  return revertResponse;
};

const createOrUpdatePriceOverride = async (previous_operation_ok, line_item) => {
  if (canCreatePriceOverride(previous_operation_ok, line_item)) {
    await createPriceOverride(line_item);
  } else if (canUpdatePriceOverride(previous_operation_ok, line_item)) {
    await patchPriceOverride(line_item);
  }
};

const updateAllOrderExcursions = async () =>
  await Promise.all(
    updateExcursionsData.value
      .map((item, index) =>
        // limit the amount of concurrent requests
        plimit(async () => {
          try {
            item.cancellation_reason =
              item.cancellation_reason === CANCELLATION_REASONS_OTHER
                ? item.otherCancellationReason
                : item.cancellation_reason;


            const use_latest_product_price = findLatestPrice(item);
            const should_update_line_item = props.excursionsData[index].status !== item.status || use_latest_product_price;
            // Sequential (line item update -> price override)
            if (should_update_line_item) {
              const { data, status } = await orders.updateOrderLineItem({
                orderId: orderId.value,
                productId: item.new_product_id,
                payload: {
                  ...item,
                  use_latest_product_price
                },
                skipErrorBus: true,
              });
              updateExcursionsData.value[index].status = item.status;
              await createOrUpdatePriceOverride(status === 200, item);
              emit("updateSavedExcursionData", data);
              return data;
            } else {
              // Simply do price override stuff
              await createOrUpdatePriceOverride(true, item);
              return { status: 200};
            }
          } catch (err) {
            savingErrorToast(
              `Error changing status of ${item.name}. Please, try it later.`
            );
            throw err;
          }
        })
      ) || []
  );

const createNewOrderExcursions = async () =>
  await Promise.all(
    newExcursionsData.value?.map((item, index) =>
      // limit the amount of concurrent requests
      plimit(async () => {
        try {
          const { data } = await orders.createOrderLineItem({
            orderId: orderId.value,
            productId: item.new_product_id,
            payload: { ...item },
            skipErrorBus: true,
          });
          emit("updateSavedExcursionData", data, item);
          removeNewExcursion(index);
          return data;
        } catch (err) {
          savingErrorToast(`Error adding ${item.title}. Please, try it later.`);
          throw err;
        }
      })
    ) || []
  );

const handleSaveExcursions = async () => {
  v$.value.$reset();
  updateV$.value.$reset();
  const isValidUpdate = await updateV$.value.$validate();
  const isValidCreate = await v$.value.$validate();
  const isValidOverride = updateExcursionsData.value.every((excursion) => {
    // Ugly and temp validation added bc Vuelidate
    // simply won't cooperate validating nested objects in an array
    if (exemptFromValidation.value.has(excursion.id)) {
      return true
    } else if ((
        excursion.status === ORDER_PRODUCT_STATUS.fulfilled
        || excursion.status === ORDER_PRODUCT_STATUS.canceled
      )
      &&
      (excursion?.price_option === OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value
        || excursion?.price_option === OPTIONS_PRICE_OVERRIDE.CUSTOM_PRICE.value
      )
    ) {
      return Boolean(excursion?.price_override?.override_reason_id); // reason required
    }
    return true;
  });

  if (!isValidOverride) {
    savingErrorToast(
      "Make sure all override reasons have a reason selected."
    );
    return;
  }

  if (!isValidUpdate) {
    savingErrorToast(
      "Cancellation reason is required when status is Canceled."
    );
    return;
  }

  if (!isValidCreate) {
    savingErrorToast("Please, provide a valid excursion to be added.");
    return;
  }

  try {
    isSaving.value = true;
    const updateResponse = await updateAllOrderExcursions();
    const createResponse = unref(newExcursionsData)?.length
      ? await createNewOrderExcursions()
      : [];
    if (
      [...updateResponse, ...createResponse].every(
        ({ status }) => status === "rejected"
      )
    ) {
      throw "Error saving data";
    }
    toast.open({
      message: "Order item status updated successfully",
      type: "success",
      position: "bottom",
      duration: SUCCESS_TIMEOUT,
    });
    queryClient.invalidateQueries({queryKey: ["order"]});
    handleCloseModal();
  } catch (exception) {
    savingErrorToast();
  } finally {
    isSaving.value = false;
    clearNewExcursionsData();
  }
};

const updateNewExcursions = (val) => {
  newExcursionsData.value =
    val?.map((item) => ({
      new_product_id: item?.id,
      name: item?.title ?? "",
      product_type: "EVENT",
      event_id: item?.event_id,
      title: item?.title ?? "",
      duration: item?.duration ?? "",
      product_price: item?.product_price ?? 0,
      quantity: 1,
      status: "Unfulfilled",
    })) || [];
  closeExcursionsSelectionModal();
};
const closeExcursionsSelectionModal = () => {
  excursionsSelectionModalIsOpen.value = false;
};

const removeNewExcursion = (index) => {
  newExcursionsData.value.splice(index, 1);
};

const clearNewExcursionsData = () => {
  newExcursionsData.value = [];
};


const resetPriceOverride = (index, resetPriceOption = false) => {
  const currentLineItem = updateExcursionsData.value[index];

  if (resetPriceOption) {
    const current_session_pricing = lineItemPriceHasChanged(currentLineItem, props.sessionPrices?.event_occurrences, LIBRARY_IDS.events);
    updateExcursionsData.value[index]["price_option"] = setPriceOverrideDropdownOption(currentLineItem, current_session_pricing);
  }

  updateExcursionsData.value[index]["price_override"] = generateDefaultPriceOverride(
    currentLineItem,
    currentLineItem["price_option"],
    lineItemHasPendingOverride(currentLineItem) ? currentLineItem["latest_unapproved_price_override"] : {},
    LIBRARY_IDS.events,
    orderId.value
  );
}

const cancelOrRevert = async (line_item) => {
  // MVP: Just revert, cancel will come later
  // if (lineItemHasPendingOverride(line_item)) {
  // ....
  //}
  await revertPriceOverride(line_item);
  handleCloseModal();
};

const buildEditableExcursion = (line_item) => {
  const current_session_pricing = lineItemPriceHasChanged(line_item, props.sessionPrices?.event_occurrences, LIBRARY_IDS.events);
  const price_dropdown_default_option = setPriceOverrideDropdownOption(line_item, current_session_pricing);
  const price_options = generatePriceOverrideOptions(line_item, current_session_pricing, price_dropdown_default_option);
  const pending_price_override = lineItemHasPendingOverride(line_item) ? line_item.latest_unapproved_price_override : {};

  return {
    current_session_pricing: current_session_pricing,
    current_price_config: showCurrentPrice(pending_price_override?.id, current_session_pricing?.total_price_in_cents, line_item?.total_price_in_cents),
    pending_price_override: !!pending_price_override?.id,
    date_range: buildDateRange(line_item),
    total_price: Boolean(Number.isFinite(line_item?.total_price_in_cents)) ? `${convertCost(centsToDollars(line_item?.total_price_in_cents))}` : line_item?.product_price,
    price_options: price_options,
    price_option: price_dropdown_default_option,
    cancellation_reason: checkOtherCancellationReason(line_item.cancellation_reason)
      ? CANCELLATION_REASONS_OTHER
      : line_item.cancellation_reason,
    otherCancellationReason: checkOtherCancellationReason(line_item.cancellation_reason)
      ? line_item.cancellation_reason
      : "",
    price_override: generateDefaultPriceOverride(line_item, price_dropdown_default_option, pending_price_override, LIBRARY_IDS.events, orderId.value),
    price_override_status: buildStatusRow(line_item),
    use_latest_product_price: undefined,
    previously_approved: lineItemCurrentlyApproved(line_item),
    disable_price_dropdown: Boolean(pending_price_override?.id),
    name: line_item?.name,
    display_price: Number.isFinite(line_item?.total_price_in_cents) && line_item?.total_price_in_cents !== 0,
    status_before_changes: line_item?.status,
  };
}

watch(
  () => props.excursionsData,
  (newExcursions) => {
    updateExcursionsData.value = cloneDeep(newExcursions).map((newExcursion) => ({
      ...newExcursion,
      ...buildEditableExcursion(newExcursion)
    }));
  },
  { immediate: true, deep: true }
);


const mainModalIsOpen = ref(modalIsOpen.value && !excursionsSelectionModalIsOpen.value);
const modalHeaderPassthrough = {
  root: {
    class: "w-3/5",
  },
  header: {
    class: ["bg-emerald-50", "min-h-65", "flex", "justify-between", "items-center", "pl-5", "pr-2", "rounded-t-lg", "border", "border-white"],
  },
}

const { keepPriceReasons, customPriceReasons, } = getOverrideReasons();
</script>

<template>
  <div>
    <ExcursionsSelectionModal
      :modal-is-open="excursionsSelectionModalIsOpen"
      :terms="programSession?.items?.terms"
      :session_travel_details="programSession?.items?.session_travel_details"
      :show-filters="false"
      :add-all-enabled="false"
      :save-on-submit="false"
      :columns-config="columnsConfig"
      @update-new-excursions="updateNewExcursions"
      @close-modal="closeExcursionsSelectionModal"
    />
    <Dialog :visible="mainModalIsOpen" :modal="true" @update:visible="handleCloseModal" :pt="modalHeaderPassthrough" dismissableMask>
      <template #header>
        <div class="font-semibold font-montserrat text-slate-700">
          {{ profileName ? `${profileName}'s ` : "" }}Excursions
        </div>
      </template>
      <template #closeicon>
        <CloseIcon size="18" stroke="#037f80" stroke-width="1.75" />
      </template>

      <!-- Content -->
      <div class="max-h-96 overflow-auto">
        <table class="w-full">
          <thead>
            <tr
              class="grid grid-cols-5 border-b-[1px] border-solid border-b-gray-200 bg-gray-125"
            >
              <th
                v-for="header of tableHeaders"
                class="font-montserrat text-sm text-left font-semibold py-3 px-2 last:w-40"
              >
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody class="bg-white">
            <tr
              class="grid grid-cols-5 border-b-[1px] border-solid border-b-gray-200 items-center text-sm font-montserrat font-medium text-pretty"
              v-for="(excursion, index) in updateExcursionsData"
              :key="excursion.id"
            >
              <td class="py-6 text-left">
                {{ excursion?.name }}
              </td>
              <td class="py-6 text-left">
                {{ excursion?.duration || '' }}
              </td>
              <td class="sm:w-65 py-6">
                {{ excursion?.date_range || '' }}
              </td>
              <td class="py-6 text-left">
                <div v-if="ORDER_PRODUCT_STATUS.fulfilled === excursion.status">
                  <Dropdown
                    v-model="excursion.price_option"
                    :disabled="excursion.disable_price_dropdown"
                    :options="excursion?.price_options"
                    optionLabel="label"
                    optionValue="value"
                    :option-disabled="(opt) => disableKeepCurrent(opt, excursion?.previously_approved)"
                    placeholder="Select price"
                    class="w-full"
                    @change="() => resetPriceOverride(index)"
                  />
                </div>
                <p v-else-if="excursion.display_price">
                {{ excursion?.total_price || '' }}
                </p>
              </td>
              <td class="sm:w-40 py-6">
                  <Dropdown
                    v-model="excursion.status"
                    :disabled="excursion.disable_price_dropdown"
                    :options="statusOptions"
                    placeholder="Select status"
                    class="w-full"
                    @change="() => resetPriceOverride(index, true)"
                  />
              </td>
              <td
                class="col-start-3 col-end-6 flex items-center justify-end pb-6"
                v-if="excursion.status === 'Canceled'"
              >
                <label
                  class="mr-2 text-sm font-semibold basis-auto shrink-0 grow-1"
                  :for="`price-for-canceled-excursion-${excursion.id}`"
                >
                  Price of Canceled Excursion
                </label>
                  <Dropdown
                    v-model="excursion.price_option"
                    :disabled="excursion.disable_price_dropdown"
                    :options="excursion?.price_options"
                    optionLabel="label"
                    optionValue="value"
                    :inputId="`price-for-canceled-excursion-${excursion.id}`"
                    placeholder="Select price"
                    class="w-full"
                    @change="() => resetPriceOverride(index)"
                  />
              </td>
              <td class="col-start-3 col-end-6 flex flex-col pb-6" v-if="excursion.price_option === OPTIONS_PRICE_OVERRIDE.CUSTOM_PRICE.value">
                <div class="flex items-center justify-end w-full mb-6 gap-4">
                  <div class="text-sm font-semibold">
                    Current Product Price: <span class="font-normal">{{ excursion.total_price }}</span>
                  </div>

                  <div class="flex items-center gap-2">
                    <label class="text-sm font-semibold">New Price $</label>
                    <InputNumber
                      v-model="excursion.price_override.override_amount_in_dollars"
                      inputId="minmaxfraction"
                      :minFractionDigits="2"
                      :maxFractionDigits="2"
                      :min="0"
                    />
                  </div>
                </div>

                <!-- Override reason -->
                <div class="flex mb-6">
                  <label class="text-sm font-semibold basis-auto shrink-0 grow-1 mr-4">Override Reason</label>
                  <Dropdown
                      v-model="excursion.price_override.override_reason_id"
                      class="w-full grow shrink-1 basis-0"
                      placeholder="Select a reason."
                      :options="customPriceReasons.events.value"
                      :inputId="`custom-price-reason-${excursion.id}`"
                      optionLabel="name"
                      optionValue="id"
                      :invalid="!excursion.price_override.override_reason_id"
                    >
                      <template #option="optionSlotProps">
                        <p>
                          {{ optionSlotProps.option.name }}
                        </p>
                        <p class="ml-4 mt-2 text-gray-600 text-sm">
                          {{ optionSlotProps.option.description }}
                        </p>
                      </template>
                    </Dropdown>
                </div>

                <!-- Notes -->
                <div class="flex">
                  <label class="text-sm font-semibold basis-auto shrink-0 grow-1 mr-4">Additional Notes</label>
                  <SimpleTextarea
                    v-model="excursion.price_override.request_notes"
                    label-name="Additional Notes"
                    outer-classes="w-full grow shrink-1 basis-0"
                    :max-length="MAX_NOTE_TEXTAREA_LIMIT"
                    :disabled="isSaving"
                  />
                </div>
              </td>
              <td class="col-start-3 col-end-6 flex items-center justify-start pb-6" v-if="excursion.price_option === OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value && excursion.current_price_config.display">
                <div class="text-sm font-semibold">
                  Current Product Price: <span class="font-normal">{{ excursion.current_price_config.currentPriceFormatted }}</span>
                </div>
              </td>
              <td class="col-start-3 col-end-6 flex items-center justify-end pb-6" v-if="excursion.price_option === OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value && [ORDER_PRODUCT_STATUS.fulfilled, ORDER_PRODUCT_STATUS.canceled].includes(excursion.status)">
                <label
                  class="mr-2 text-sm font-semibold basis-auto shrink-0 grow-1"
                  :for="`keep-price-reason-${excursion.id}`"
                >
                  Keep Order Price Reason
                </label>
                <Dropdown
                  v-model="excursion.price_override.override_reason_id"
                  class="w-full grow shrink-1 basis-0"
                  placeholder="Select a reason."
                  :options="keepPriceReasons.events.value"
                  :inputId="`keep-price-reason-${excursion.id}`"
                  optionLabel="name"
                  optionValue="id"
                  :invalid="!exemptFromValidation.has(excursion.id) && !excursion.price_override.override_reason_id"
                >
                  <template #option="optionSlotProps">
                    <p>
                      {{ optionSlotProps.option.name }}
                    </p>
                    <p class="ml-4 mt-2 text-gray-600 text-sm">
                      {{ optionSlotProps.option.description }}
                    </p>
                  </template>
                </Dropdown>
              </td>
              <td
                class="col-start-3 col-end-6 flex items-center justify-end pb-6"
                v-if="excursion.status === 'Canceled'"
              >
                <label
                  class="mr-2 text-sm font-semibold basis-auto shrink-0 grow-1"
                  :for="`reason-for-cancellation-${excursion.id}`"
                >
                  Reason for Cancellation
                </label>
                  <Dropdown
                    v-model="excursion.cancellation_reason"
                    class="w-full grow shrink-1 basis-0"
                    placeholder="Select cancellation reason."
                    :options="EXCURSION_CANCELLATION_REASONS"
                    :inputId="`reason-for-cancellation-${excursion.id}`"
                    optionLabel="label"
                    optionValue="label"
                  />
              </td>
              <td
                id="product-reason-cancellation-data"
                class="col-start-3 col-end-6 flex items-center justify-end pb-6"
                v-if="
                  excursion.status === 'Canceled' &&
                  excursion.cancellation_reason === 'Other'
                "
              >
                <SimpleTextarea
                  v-model="excursion.otherCancellationReason"
                  label-name="Cancellation Reason"
                  outer-classes="w-full"
                  :max-length="MAX_NOTE_TEXTAREA_LIMIT"
                  :disabled="isSaving"
                />
              </td>
              <td class="col-start-3 col-end-6 flex flex pb-6 gap-2 font-montserrat" v-if="excursion.price_override_status.display">
                <p class="text-sm font-semibold">Override Request Status: </p>
                <!-- Override Icon -->
                <span class="material-symbols-outlined" :class="excursion.price_override_status.labelColor">
                  {{ excursion.price_override_status.icon}}
                </span>
                <p> {{ excursion.price_override_status.labelStatus }} </p>
                <p>
                  <span v-if="excursion.price_override_status.displayRevert" class="uppercase text-sm font-semibold text-teal-900 cursor-pointer" @click.prevent="cancelOrRevert(excursion)">
                    | {{ excursion.price_override_status.revertLabel }}
                  </span>
                </p>
              </td>
            </tr>
            <tr
              class="grid grid-cols-5 border-b-[1px] border-solid border-b-gray-200 items-center"
              v-for="newExcursion in newExcursionsData"
              :key="newExcursion.id"
            >
              <td class="py-6 text-left">
                {{ newExcursion?.title || '' }}
              </td>
              <td class="py-6 text-left">
                {{ newExcursion?.duration || '' }}
              </td>
              <td class="sm:w-65 py-6">
                {{ newExcursion.date_time }}
              </td>
              <td class="py-6 text-left">
                {{ newExcursion?.product_price || '' }}
              </td>
              <td class="text-right">
                <div class="flex">
                  <Dropdown
                    v-model="newExcursion.status"
                    :options="statusOptions"
                    placeholder="Select status"
                  />
                  <button
                    class="duration-300 hover:scale-110 hover:text-red-100"
                    @click="removeNewExcursion(index)"
                  >
                    <TrashIcon class="h-5" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- New Excursion -->
      <button
        class="mt-4 flex w-fit cursor-pointer items-center text-teal-900 duration-300 hover:scale-110"
        @click="() => openExcursionsSelectionModal()"
      >
        <PlusIcon />
      </button>

      <!-- Actions -->
      <div class="flex justify-end">
        <BaseButton outlined :on-click="handleCloseModal" class="px-7"
          >Cancel</BaseButton
        >
        <ButtonWithSpinner
          :prop-loading="isSaving"
          class="mr-0 text-base font-semibold"
          data-cy="save-order-room-button"
          variant="secondary"
          variant-type="normal"
          @click.prevent="handleSaveExcursions"
        >
          <span>Save</span>
        </ButtonWithSpinner>
      </div>
    </Dialog>
  </div>
</template>
